'use client'

import { useContext, useEffect, useMemo, useState } from 'react'
import { getApiBaseUrl } from '@/lib/config'
import { normalizeMarketPayload, type MarketPayload } from '@/lib/market-rates'
import {
  MarketRatesContext,
  useMarketLiveSubscription,
} from '@/components/market/MarketRatesProvider'

export function useMarketRates(options?: { live?: boolean }) {
  const ctx = useContext(MarketRatesContext)
  const live = options?.live !== false
  const apiBase = useMemo(() => getApiBaseUrl(), [])
  const [localMarket, setLocalMarket] = useState<MarketPayload | null>(null)
  const [localLoading, setLocalLoading] = useState(!ctx)

  useMarketLiveSubscription(Boolean(ctx) && live)

  useEffect(() => {
    if (ctx) return

    let cancelled = false
    let es: EventSource | null = null

    if (!live) {
      void (async () => {
        try {
          setLocalLoading(true)
          const res = await fetch(`${apiBase}/api/market/latest`, { cache: 'no-store' })
          const json = await res.json()
          if (cancelled) return
          setLocalMarket(normalizeMarketPayload(json?.data))
        } catch {
          if (!cancelled) setLocalMarket(null)
        } finally {
          if (!cancelled) setLocalLoading(false)
        }
      })()
      return () => {
        cancelled = true
      }
    }

    if (typeof EventSource === 'undefined') {
      setLocalLoading(false)
      return
    }

    const open = () => {
      if (cancelled) return
      es?.close()
      es = new EventSource(`${apiBase}/api/market/stream`)
      es.onmessage = (ev) => {
        try {
          const json = JSON.parse(ev.data)
          const next = normalizeMarketPayload(json?.data)
          if (next) setLocalMarket(next)
          setLocalLoading(false)
        } catch {
          // ignore
        }
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        es?.close()
        es = null
      } else {
        open()
      }
    }
    const onOnline = () => {
      if (document.visibilityState !== 'hidden') open()
    }

    open()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('online', onOnline)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('online', onOnline)
      es?.close()
    }
  }, [apiBase, ctx, live])

  if (ctx) {
    return {
      market: ctx.market,
      loading: ctx.loading,
      busy: ctx.busy,
    }
  }

  return {
    market: localMarket,
    loading: localLoading,
    busy: localLoading,
  }
}
